import { useState, useRef, useEffect } from 'react';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT, CTW_NODE_API_ENDPOINT } from '../utils';
import { toast } from '../common/Popover';
import { TargetingSearchResponse } from '../interfaces/apiTypes';
import { handleApiError } from '../utils/errorHandler';
import { buildEstimateTargetingSpec, buildTargetingSpec } from '../utils/campaignUtils';

interface Location {
    key: string;
    name: string;
    type?: string;
    country_code?: string;
    country_name?: string;
    region?: string;
    region_id?: number;
    supports_region?: boolean;
    supports_city?: boolean;
    geo_hierarchy_name?: string;
}

export const useTargeting = (
    selectedAdAccountId: string,
    initialSelectedLocations: Location[] = [],
    initialExcludedLocations: Location[] = [],
    initialSelectedTargeting: Array<{ id: string; name: string; type: string }> = []
) => {

    const [selectLocations, setSelectLocations] = useState<Location[]>([]);
    const [excludedLocations, setExcludedLocations] = useState<Location[]>([]);
    const [selectedTargeting, setSelectedTargeting] = useState<
        Array<{ id: string; name: string; type: string }>
    >([]);
    const [targetingSearch, setTargetingSearch] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const [targetingOptions, setTargetingOptions] = useState<
        {
            value: string;
            label: string;
            type: string;
        }[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [locationOptions, setLocationOptions] = useState<
        {
            value: string;
            label: string;
            type: string;
            country_name?: string;
            country_code?: string;
            region?: string;
            region_id?: number;
            supports_region?: boolean;
            supports_city?: boolean;
            geo_hierarchy_name?: string;
        }[]
    >([]);
    const [targetingOptionsData, setTargetingOptionsData] = useState<any[]>([]);
    const [isSearchingLocations, setIsSearchingLocations] = useState(false);
    const [isSearchingInterests, setIsSearchingInterests] = useState(false);
    const [estimatedReach, setEstimatedReach] = useState<number | null>(null);
    const locationSearchTimerRef = useRef<any>(null);
    const interestSearchTimerRef = useRef<any>(null);
    const estimateTimerRef = useRef<any>(null);

    // Sync internal options with external selections
    useEffect(() => {
        const allInitial = [...initialSelectedLocations, ...initialExcludedLocations];
        // console.log(allInitial, "[]");

        if (allInitial.length > 0) {
            const uniqueInitial = Array.from(new Map(allInitial.map(item => [item.key, item])).values());

            // Update locationOptions to include initial selections
            setLocationOptions(prev => {
                const initialOptions = uniqueInitial.map((l) => ({
                    value: l.key,
                    label: l.name,
                    type: l.type || 'city',
                    country_code: l.country_code,
                    country_name: l.country_name,
                    region: l.region,
                    region_id: l.region_id,
                    supports_city: l.supports_city,
                    supports_region: l.supports_region,
                    geo_hierarchy_name: l.geo_hierarchy_name,
                }));
                const combined = [...prev, ...initialOptions];
                return Array.from(new Map(combined.map(item => [item.value, item])).values());
            });
        }
    }, [initialSelectedLocations, initialExcludedLocations]);

    // Sync internal options with external selections
    useEffect(() => {
        const allInitial = [...initialSelectedTargeting];
        if (allInitial.length > 0) {
            const uniqueInitial = Array.from(new Map(allInitial.map(item => [item.id, item])).values());

            // Update locationOptions to include initial selections
            setTargetingOptions(prev => {
                const initialOptions = uniqueInitial.map((l) => ({
                    value: l.id,
                    label: l.name,
                    type: l.type || '   ',
                }));
                const combined = [...prev, ...initialOptions];
                return Array.from(new Map(combined.map(item => [item.value, item])).values());
            });
        }
    }, [initialSelectedTargeting]);


    const searchLocations = async (searchTerm: string) => {
        if (!searchTerm.trim()) return;

        if (locationSearchTimerRef.current) {
            clearTimeout(locationSearchTimerRef.current);
        }

        locationSearchTimerRef.current = setTimeout(async () => {
            try {
                setIsSearchingLocations(true);
                const endpoint = `v2/meta-integration/metaGeoSearch?adAccountId=${selectedAdAccountId}&q=${encodeURIComponent(searchTerm)}`;

                const response = await coreAPIService.get<TargetingSearchResponse>(
                    CTW_API_ENDPOINT,
                    endpoint
                );

                if (response.meta.status && Array.isArray(response.data)) {
                    const rawResults = response.data;
                    // console.log(rawResults);

                    const locations: Location[] = [];

                    rawResults.forEach((item: any) => {
                        locations.push({
                            key: item.key,
                            name: item.name,
                            type: item.type?.toLowerCase() || 'city',
                            country_code: item.countryCode,
                            country_name: item.countryName,
                            region: item.region,
                            region_id: item.regionId,
                            supports_region: item.supportsRegion,
                            supports_city: item.supportsCity,
                            geo_hierarchy_name: item.geoHierarchyName,
                        });
                    });

                    const newLocations = locations.filter(
                        (loc) => !selectLocations.some((existing) => existing.key === loc.key) &&
                            !excludedLocations.some((existing) => existing.key === loc.key)
                    );
                    const allLocations = [...selectLocations, ...excludedLocations, ...newLocations];

                    // Deduplicate based on key
                    const uniqueLocations = Array.from(new Map(allLocations.map(item => [item.key, item])).values());

                    setLocationOptions(
                        uniqueLocations.map((l) => ({
                            value: l.key,
                            label: l.name,
                            type: l.type || 'city',
                            country_name: l.country_name,
                            country_code: l.country_code,
                            region: l.region,
                            region_id: l.region_id,
                            supports_city: l.supports_city,
                            supports_region: l.supports_region,
                            geo_hierarchy_name: l.geo_hierarchy_name,
                        }))
                    );
                }
            } catch (error) {
                handleApiError(error, 'v2/meta-integration/metaGeoSearch', 'Failed to search locations');
            } finally {
                setIsSearchingLocations(false);
            }
        }, 800);
    };

    const searchInterests = async (searchTerm: string) => {
        if (!searchTerm.trim()) return;

        if (interestSearchTimerRef.current) {
            clearTimeout(interestSearchTimerRef.current);
        }

        interestSearchTimerRef.current = setTimeout(async () => {
            try {
                setIsSearchingInterests(true);
                const endpoint = `v2/meta-integration/metaInterestSearch?adAccountId=${selectedAdAccountId}&q=${encodeURIComponent(searchTerm)}`;

                const response = await coreAPIService.get<TargetingSearchResponse>(
                    CTW_API_ENDPOINT,
                    endpoint
                );

                if (response.meta.status && Array.isArray(response.data)) {
                    const rawResults = response.data;
                    const interests: Array<{ id: string; name: string; type: string }> = [];

                    rawResults.forEach((item: any) => {
                        // Map API response fields to our internal structure
                        // The API might return 'topic', 'type', or we default to 'interest'
                        const type = item.topic || item.type || 'interest';
                        interests.push({
                            id: item.id || item.key,
                            name: item.name,
                            type: type.toLowerCase(),
                        });
                    });

                    const newInterests = interests.filter(
                        (interest) => !selectedTargeting.some((existing) => existing.id === interest.id)
                    );
                    const allInterests = [...selectedTargeting, ...newInterests];
                    setTargetingOptionsData(allInterests);
                    setTargetingOptions(
                        allInterests.map((item) => ({
                            value: item.id,
                            label: item.name,
                            type: item.type,
                            estimated_size: 'N/A',
                        }))
                    );
                }
            } catch (error) {
                handleApiError(error, 'v2/meta-integration/metaInterestSearch', 'Failed to search interests');
            } finally {
                setIsSearchingInterests(false);
            }
        }, 800);
    };

    const fetchDeliveryEstimate = async (
        locations: Location[],
        excluded: Location[],
        targeting: Array<{ id: string; name: string; type: string }>,
        age: [number, number] = [18, 65],
        customAudience?: Array<{ id: string; name: string }>
    ) => {
        if (!selectedAdAccountId) return;

        if (estimateTimerRef.current) {
            clearTimeout(estimateTimerRef.current);
        }

        setLoading(true);
        estimateTimerRef.current = setTimeout(async () => {
            try {
                const targetingSpec = buildEstimateTargetingSpec(
                    true,
                    customAudience?.[0]?.id ? [{ id: customAudience?.[0]?.id }] : undefined,
                    locations,
                    excluded,
                    age,
                    targeting,
                    ['facebook', 'instagram'] // Default platform for estimate
                );

                const response = await coreAPIService.post<{
                    meta: { success: boolean; code: number; message: string };
                    data: number;
                }>(
                    CTW_NODE_API_ENDPOINT,
                    `/management/v1/delivery_estimate?adAccountId=${selectedAdAccountId}`,
                    {
                        targeting_spec: targetingSpec,
                        optimization_goal: 'REACH'
                    }
                );

                if (response.meta.success && response.data) {
                    setEstimatedReach(response.data || 0);
                } else {
                    setEstimatedReach(null);
                }
            } catch (error) {
                console.error('Failed to fetch delivery estimate', error);
                setEstimatedReach(null);
            } finally {
                setLoading(false);
            }
        }, 1000);
    };

    const handleTargetingChange = (ids: string[]) => {
        if (ids.length > 5) {
            alert('Maximum 5 targeting options allowed');
            return;
        }
        const selectedTargets = targetingOptionsData
            .filter((t) => ids.includes(t.id))
            .map(({ id, name, type }) => ({ id, name, type }));
        setSelectedTargeting(selectedTargets);
    };

    return {
        selectLocations,
        excludedLocations,
        selectedTargeting,
        targetingSearch,
        setTargetingSearch,
        locationSearch,
        setLocationSearch,
        targetingOptions,
        locationOptions,
        isSearchingLocations,
        isSearchingInterests,
        searchLocations,
        searchInterests,
        handleTargetingChange,
        estimatedReach,
        fetchDeliveryEstimate,
        loading,
        // loading // Returned loading state
    };
};
